import authService from "@features/authentication/services/authService";
import logger from "@logger";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Authentication state for the dating app
 */
interface AuthState {
  /** Currently authenticated user */
  userId: string | null;
  /** Single status that captures the full auth lifecycle */
  status: "pending" | "authenticated" | "unauthenticated" | "error";
  /** Authentication error message */
  error: string | null;
}

/**
 * Authentication actions
 */
interface AuthActions {
  /** Set authentication status */
  setStatus: (status: AuthState["status"]) => void;
  /** Set authentication error */
  setError: (error: string | null) => void;
  /** Clear all authentication state */
  clearAuth: () => void;
}

/**
 * Combined authentication store type
 */
export type AuthStore = AuthState & AuthActions;

/**
 * Initial authentication state
 */
const initialState: AuthState = {
  userId: null,
  status: "pending", // Start pending, not loading
  error: null,
};

/**
 * Zustand store for authentication state management
 *
 * Handles user authentication state and auth status.
 * Integrates with Auth Service for actual authentication.
 * Automatically initializes authentication state on creation.
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => {
      // Initialize authentication state
      authService.onAuthStateChanged(async (user) => {
        try {
          if (user) {
            set(
              {
                userId: user.uid,
                status: !user.uid ? "unauthenticated" : "authenticated",
                error: null,
              },
              false,
              "auth/setUser"
            );
          } else {
            // User is signed out, clear auth state
            set(
              {
                userId: null,
                status: "unauthenticated",
                error: null,
              },
              false,
              "auth/clearAuth"
            );
          }
        } catch (error) {
          logger.error("Failed to initialize auth:", error);
          set(
            {
              status: "error",
              error: "Failed to initialize authentication",
            },
            false,
            "auth/setError"
          );
        }
      });

      return {
        ...initialState,

        setStatus: (status) => set((state) => ({ ...state, status }), false, "auth/setStatus"),

        setError: (error) => set((state) => ({ ...state, error, status: "error" }), false, "auth/setError"),

        clearAuth: () => set(() => initialState, false, "auth/clearAuth"),
      };
    },
    {
      name: "fitties-auth-store",
      enabled: import.meta.env.DEV,
    }
  )
);
